import { useState } from 'react';
import { useCategories, useCreateCategory, useUpdateCategory, useDeleteCategory, useRecalculateCategoryPrices } from '@/hooks/useProducts';
import { ProductCategory } from '@/types';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import CategoryFormModal from '@/components/CategoryFormModal';
import { Plus, Edit, Trash2, Tag, Calculator } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Categories() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<ProductCategory | null>(null);

  const { data: categories, isLoading } = useCategories();
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();
  const recalculateCategoryPrices = useRecalculateCategoryPrices();

  const handleRecalculate = async (categoryId: number) => {
    try {
      const result = await recalculateCategoryPrices.mutateAsync(categoryId);
      toast.success(`Se recalcularon precios de ${result.updated_count} productos`);
    } catch {
      toast.error('Error al recalcular precios');
    }
  };

  const handleOpenModal = (category?: ProductCategory) => {
    setEditingCategory(category ?? null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
  };

  const handleSave = async (data: any) => {
    try {
      if (editingCategory) {
        await updateCategory.mutateAsync({ id: editingCategory.id, data });
      } else {
        await createCategory.mutateAsync(data);
      }
    } catch (error) {
      console.error('Error saving category:', error);
      throw error;
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteCategory.mutateAsync(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const isSubmitting = createCategory.isPending || updateCategory.isPending;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-taupe-900">Categorías</h1>
          <p className="text-sm text-taupe-500">Gestiona las categorías de productos</p>
        </div>
        <Button onClick={() => handleOpenModal()}>
          <Plus className="h-4 w-4 mr-2" />
          Nueva Categoría
        </Button>
      </div>

      <div className="card">
        {isLoading ? (
          <div className="text-center py-8 text-taupe-500">Cargando...</div>
        ) : categories && categories.length > 0 ? (
          <div className="divide-y divide-taupe-100">
            {categories.map((category) => {
              const method = category.pricing_method || 'multiplier';
              const cashVal = Number(category.cash_multiplier ?? 0);
              const methodLabel =
                method === 'margin' ? `${cashVal}% margen` :
                method === 'percentage' ? `+${cashVal}% markup` :
                `×${cashVal}`;
              const hasThresholds =
                category.margin_min != null || category.margin_target != null;

              return (
                <div
                  key={category.id}
                  className="flex items-center justify-between py-4 px-2 hover:bg-cream-50 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-taupe-100 rounded-lg flex items-center justify-center">
                      <Tag className="h-5 w-5 text-taupe-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-taupe-900">{category.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-taupe-500 flex-wrap">
                        {category.description && (
                          <>
                            <span>{category.description}</span>
                            <span className="text-taupe-300">·</span>
                          </>
                        )}
                        <span className="inline-flex items-center gap-1 text-taupe-700">
                          <Calculator className="h-3 w-3" />
                          {methodLabel}
                        </span>
                        {hasThresholds && (
                          <>
                            <span className="text-taupe-300">·</span>
                            <span className="inline-flex items-center gap-1 text-cream-700">
                              mín {category.margin_min ?? '—'}% / obj {category.margin_target ?? '—'}%
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenModal(category)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteConfirm(category)}
                      className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-taupe-500">
            No hay categorías creadas
          </div>
        )}
      </div>

      {/* Modal de crear/editar */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
        size="xl"
      >
        <CategoryFormModal
          category={editingCategory}
          onSave={handleSave}
          onClose={handleCloseModal}
          isLoading={isSubmitting}
          onRecalculate={handleRecalculate}
          isRecalculating={recalculateCategoryPrices.isPending}
        />
      </Modal>

      {/* Modal de confirmación de eliminación */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Eliminar Categoría"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-taupe-700">
            ¿Estás seguro de que deseas eliminar la categoría{' '}
            <strong>{deleteConfirm?.name}</strong>?
          </p>
          <p className="text-sm text-taupe-500">
            Los productos asociados a esta categoría quedarán sin categoría asignada.
          </p>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={deleteCategory.isPending}
            >
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
